import { Injectable, inject, signal, computed, Injector } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { User } from '../models/user.model';
import { ApiService } from './api.service';
import { UtilsService } from './utils.service';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { WorkingContract } from '../models/working_contract.model';
import { EmployeeCalendarRegisterWithRelationsSerialized } from '../models/employee_calendar_register.model';
import dayjs from 'dayjs';
import { PlatformService } from './platform.service';
import { environment } from 'src/environments/environment';
import { ForceSetupDoNotDisturbShortcutsDialogComponent } from '../components/main/shared/force-setup-do-not-disturb-shortcuts-dialog/force-setup-do-not-disturb-shortcuts-dialog.component';
import { Router } from '@angular/router';
import { UserService } from './user.service';
import { MeetingAttendanceDialogComponent } from '../components/main/shared/meeting-attendance-dialog/meeting-attendance-dialog.component';
import { PendingMeetingAttendance } from '../models/pending-meeting-attendance.interface';
import { ActionsRequired } from '../models/actions-required.interface';
import { DayOfWeekEnum, fromDayOfWeekIso } from '../enums/day-of-week.enum';
import { WorkingContractWeeklySchedule } from '../models/working_contract_weekly_schedule.model';
import { isDesktopWebAllowedUrl, shouldForceDesktopApp } from './platform.service';

@Injectable({
  providedIn: 'root',
})
export class EmployeeService {
  private apiService = inject(ApiService);
  protected utils = inject(UtilsService);
  private dialog = inject(MatDialog);
  private platform = inject(PlatformService);
  protected router = inject(Router);

  private isMeetingAttendanceDialogOpening = false;

  // Ongoing task variable
  private todaysTasks: CalendarRegisterLocalItem[] = [];
  private currentTaskSubject: BehaviorSubject<{
    current: CalendarRegisterLocalItem;
    next: CalendarRegisterLocalItem;
    showLiveIndicator: boolean;
  }> = new BehaviorSubject({
    current: null as any,
    next: null as any,
    showLiveIndicator: false as boolean,
  });
  public currentTask: Observable<{
    current: CalendarRegisterLocalItem;
    next: CalendarRegisterLocalItem;
    showLiveIndicator: boolean;
  }> = this.currentTaskSubject.asObservable();

  // Guard status
  private currentGuardStatusSubject: BehaviorSubject<{ active: boolean; company_product_names: string | undefined }> = new BehaviorSubject({
    active: false,
    company_product_names: undefined,
  } as any);
  public currentGuardStatus: Observable<{ active: boolean; company_product_names: string | undefined }> =
    this.currentGuardStatusSubject.asObservable();

  // Meeting mode
  private meetingModeCompanyProductLocalStorageKey: string = 'meeting_mode_company_product_id';
  private _meetingModeCompanyProductId: BehaviorSubject<number> = new BehaviorSubject(null as any);
  public meetingModeCompanyProduct: Observable<number> = this._meetingModeCompanyProductId.asObservable();
  private meetingModeEnabledLocalStorageKey: string = 'meeting_mode_enabled';
  private _meetingModeEnabled: BehaviorSubject<boolean> = new BehaviorSubject(false);
  public meetingModeEnabled: Observable<boolean> = this._meetingModeEnabled.asObservable();

  // Current working contract from user
  private currentWorkingContractActiveSubject: BehaviorSubject<WorkingContract> = new BehaviorSubject(null as any);
  public workingContractActive: Observable<WorkingContract> = this.currentWorkingContractActiveSubject.asObservable();
  private currentWorkingContractWeeklySchedule: WorkingContractWeeklySchedule | null = null;

  // Active CompanyProducts
  public active_company_products_signal = signal<
    { company_product_id: number; company_product_name: string; department_category_id: number; slug: string; logo_url?: string }[]
  >([]);

  // Force Activate
  private forceSetupDoNotDisturbShortcutsDialogRef: MatDialogRef<ForceSetupDoNotDisturbShortcutsDialogComponent> | undefined;

  // meetings already stopped from meeting mode
  private meetingsAlreadyStoppedFromMeetingModeLocalStorageKey: string = 'meetings_already_stopped_from_meeting_mode_ecr_ids';

  // Update hours at top menu
  private updateHoursTopMenu: Subject<void> = new Subject<void>();

  public actionsRequired = signal<ActionsRequired>({
    pending_schedule_change_requests_count: 0,
    pending_weekly_schedule_exception_requests_count: 0,
    pending_absence_requests_count: 0,
    pending_learning_resource_requests_count: 0,
    pending_extra_hours_requests_count: 0,
    is_extra_hours_responsible: false,
    pending_responsible_remote_work_requests_count: 0,
    pending_responsible_driving_requests_count: 0,
    is_driving_responsible: false,
    is_remote_work_responsible: false,
    pending_meeting_attendances_count: 0,
    pending_potential_clients_to_contact_count: 0,
    pending_invoice_conciliations_count: 0,
    pending_slack_chat_connections_count: 0,
    pending_unassigned_messaging_conversations_count: 0,
    pending_unassigned_workspaces_count: 0,
    pending_bisual_internal_team_slack_channel_config_count: 0,
    pending_bisual_internal_team_slack_channel_config_company_product_ids: [],
    pending_bot_not_member_messaging_conversation_company_product_ids: [],
    pending_unconfigured_applications_count: 0,
    pending_unconfigured_applications_company_product_ids: [],
  });

  public totalActionsRequired = computed((): number => {
    const actions = this.actionsRequired();

    const counts_sum = Object.values(actions).reduce((sum: number, value): number => {
      return sum + (typeof value === 'number' ? value : 0);
    }, 0);

    return counts_sum + actions.pending_bot_not_member_messaging_conversation_company_product_ids.length;
  });

  public pendingSlackChatConnectionsCount = computed((): number => this.actionsRequired().pending_slack_chat_connections_count);

  public hasPendingSlackChatConnections = computed((): boolean => this.pendingSlackChatConnectionsCount() > 0);

  private destroy$ = new Subject<void>();

  private meetingAttendanceDialogRef: MatDialogRef<MeetingAttendanceDialogComponent> | null = null;
  private viewportForceDesktopListenerInitialized: boolean = false;
  private forceDesktopRecheckTimeout: ReturnType<typeof setTimeout> | null = null;

  private readonly injector = inject(Injector);

  init(user: User, force_employees_to_use_desktop_app: boolean) {
    this.resetSessionState();

    this.initViewportForceDesktopValidationListener(user, force_employees_to_use_desktop_app);

    this.loadCurrentActiveCompanyProducts(user.id);
    this.loadCurrentWorkingContract(user);
    this.loadCurrentGuardianStatus();
    this.loadRequiredActions();
    this.initMeetingModeStatus();
    this.redirectToDesktopAppPageIfNecessary(user, force_employees_to_use_desktop_app);
    this.forceIfNecessaryToSetupDoNotDisturbShortcutsOnMac(user);
  }

  isMeetingOngoing(company_product_id: number | null = null) {
    return company_product_id != null
      ? this._meetingModeEnabled.value && this._meetingModeCompanyProductId.value == company_product_id
      : this._meetingModeEnabled.value;
  }

  startMeetingMode(company_product_id: number | undefined = undefined) {
    if (!this.isMeetingOngoing()) {
      localStorage.setItem(this.meetingModeEnabledLocalStorageKey, 'true');
      this._meetingModeEnabled.next(true);
      if (company_product_id != null) {
        localStorage.setItem(this.meetingModeCompanyProductLocalStorageKey, company_product_id.toString());
        this._meetingModeCompanyProductId.next(company_product_id);
      }
      this.platform.enableDoNotDisturbIfAvailable();
    }
  }

  stopMeetingMode(): void {
    const current_ecr: CalendarRegisterLocalItem | undefined =
      this.currentTaskSubject.value && this.currentTaskSubject.value.current && this.currentTaskSubject.value.current.id
        ? this.currentTaskSubject.value.current
        : undefined;
    localStorage.removeItem(this.meetingModeEnabledLocalStorageKey);
    localStorage.removeItem(this.meetingModeCompanyProductLocalStorageKey);
    if (
      current_ecr &&
      current_ecr.id &&
      current_ecr.call_link &&
      current_ecr.company_product_id &&
      +current_ecr.company_product_id == this._meetingModeCompanyProductId.value
    ) {
      this.addEcrToMeetingsAlreadyStoppedFromMeetingMode(current_ecr.id);
    }
    this._meetingModeEnabled.next(false);
    this._meetingModeCompanyProductId.next(null as any);
    this.platform.disableDoNotDisturbIfAvailable();

    this.checkAndOpenMeetingAttendanceDialog();
  }

  reloadTodaysTasks(user: User) {
    this.todaysTasks = [];
    this.currentTaskSubject.next(null as any);
    this.loadTodaysTasks(user);
  }

  logout() {
    this.stopMeetingMode();
    this.cleanMeetingsAlreadyStoppedFromMeetingMode();
    this.resetSessionState();
  }

  getActiveCompanyProducts() {
    return this.active_company_products_signal();
  }

  getCurrentTask(): any {
    return this.currentTaskSubject.value;
  }

  /**
   * Viewport edge case: guard does not re-run on resize alone.
   * If desktop web must use the app and the current URL is not allowlisted, redirect.
   */
  redirectToDesktopAppPageIfNecessary(user: User, force_employees_to_use_desktop_app: boolean) {
    if (
      !shouldForceDesktopApp(
        user,
        force_employees_to_use_desktop_app,
        this.platform.isElectronApp(),
        this.platform.isDesktop(),
        environment.environment
      )
    ) {
      return;
    }

    if (isDesktopWebAllowedUrl(this.router.url)) {
      return;
    }

    void this.router.navigate(['/employees/use-desktop-app'], {
      queryParams: { returnUrl: this.router.url.split('?')[0] },
      replaceUrl: true,
    });
  }

  async recheckToForceSetupDoNotDisturbShortcutsDialog() {
    if (this.platform.isElectronApp()) {
      const ok: boolean = await window.electron.checkDoNotDisturbShortcuts();
      if (ok && this.forceSetupDoNotDisturbShortcutsDialogRef) {
        this.forceSetupDoNotDisturbShortcutsDialogRef.close();
      } else if (!ok) {
        alert('No se ha configurado aún.');
      }
    }
  }

  onActionsRequiredUpdated(data: ActionsRequired): void {
    this.actionsRequired.set(data);
    this.processPendingMeetingAttendances(data);
  }

  refreshActionsRequired(): void {
    this.loadRequiredActions();
  }

  private checkAndOpenMeetingAttendanceDialog(): void {
    if (this.isMeetingOngoing()) {
      return;
    }

    const isThereAlreadyAnOpenDialog = this.meetingAttendanceDialogRef != null;

    if (isThereAlreadyAnOpenDialog) {
      return;
    }

    if (this.isMeetingAttendanceDialogOpening) {
      return;
    }

    this.isMeetingAttendanceDialogOpening = true;

    this.apiService
      .getPendingMeetingAttendances()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (pendingMeetingAttendances: PendingMeetingAttendance[]): void => {
          const hasPendingMeetingAttendances = pendingMeetingAttendances.length > 0;

          if (!hasPendingMeetingAttendances) {
            this.isMeetingAttendanceDialogOpening = false;
            return;
          }

          if (this.meetingAttendanceDialogRef != null) {
            this.isMeetingAttendanceDialogOpening = false;
            return;
          }

          const currentUser = this.injector.get(UserService).getCurrentUser();

          this.meetingAttendanceDialogRef = this.dialog.open(MeetingAttendanceDialogComponent, {
            width: '600px',
            data: {
              pendingMeetingAttendances,
              currentUserId: currentUser.id,
            },
          });

          this.isMeetingAttendanceDialogOpening = false;

          this.meetingAttendanceDialogRef
            .afterClosed()
            .pipe(takeUntil(this.destroy$))
            .subscribe((): void => {
              this.meetingAttendanceDialogRef = null;

              if (currentUser) {
                this.loadRequiredActions();
              }
            });
        },
        error: (): void => {
          this.isMeetingAttendanceDialogOpening = false;
        },
      });
  }

  sendUpdateHoursTopMenu() {
    this.updateHoursTopMenu.next();
  }

  getUpdateHoursTopMenu() {
    return this.updateHoursTopMenu.asObservable();
  }

  private resetSessionState(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.destroy$ = new Subject<void>();
    this.meetingAttendanceDialogRef = null;
    this.isMeetingAttendanceDialogOpening = false;
    this.active_company_products_signal.set([]);
    this.resetActionsRequired();
  }

  private resetActionsRequired(): void {
    this.actionsRequired.set({
      pending_schedule_change_requests_count: 0,
      pending_weekly_schedule_exception_requests_count: 0,
      pending_absence_requests_count: 0,
      pending_learning_resource_requests_count: 0,
      pending_extra_hours_requests_count: 0,
      is_extra_hours_responsible: false,
      pending_responsible_remote_work_requests_count: 0,
      pending_responsible_driving_requests_count: 0,
      is_driving_responsible: false,
      is_remote_work_responsible: false,
      pending_meeting_attendances_count: 0,
      pending_potential_clients_to_contact_count: 0,
      pending_invoice_conciliations_count: 0,
      pending_slack_chat_connections_count: 0,
      pending_unassigned_messaging_conversations_count: 0,
      pending_unassigned_workspaces_count: 0,
      pending_bisual_internal_team_slack_channel_config_count: 0,
      pending_bisual_internal_team_slack_channel_config_company_product_ids: [],
      pending_bot_not_member_messaging_conversation_company_product_ids: [],
      pending_unconfigured_applications_count: 0,
      pending_unconfigured_applications_company_product_ids: [],
    });
  }

  private cleanMeetingsAlreadyStoppedFromMeetingMode() {
    localStorage.removeItem(this.meetingsAlreadyStoppedFromMeetingModeLocalStorageKey);
  }

  private addEcrToMeetingsAlreadyStoppedFromMeetingMode(ecr_id: number) {
    const ecr_ids = this.getEcrIdsOfMeetingsAlreadyStoppedFromMeetingMode();
    if (!this.isEcrIdOnMeetingsAlreadyStoppedFromMeetingMode(ecr_id)) {
      ecr_ids.push(ecr_id);
    }
    localStorage.setItem(this.meetingsAlreadyStoppedFromMeetingModeLocalStorageKey, JSON.stringify(ecr_ids));
  }

  private getEcrIdsOfMeetingsAlreadyStoppedFromMeetingMode(): number[] {
    const str = localStorage.getItem(this.meetingsAlreadyStoppedFromMeetingModeLocalStorageKey);
    let ecr_ids: number[] = [];
    if (str) ecr_ids = JSON.parse(str);
    return ecr_ids;
  }

  private isEcrIdOnMeetingsAlreadyStoppedFromMeetingMode(ecr_id: number) {
    const ecr_ids = this.getEcrIdsOfMeetingsAlreadyStoppedFromMeetingMode();
    return ecr_ids.indexOf(ecr_id) !== -1;
  }

  private initMeetingModeStatus() {
    const is_meeting_mode_enabled = localStorage.getItem(this.meetingModeEnabledLocalStorageKey);
    const company_product_id = localStorage.getItem(this.meetingModeCompanyProductLocalStorageKey);

    if (is_meeting_mode_enabled === 'true' && company_product_id != null) {
      this.startMeetingMode(+company_product_id);
    } else {
      localStorage.removeItem(this.meetingModeEnabledLocalStorageKey);
      localStorage.removeItem(this.meetingModeCompanyProductLocalStorageKey);

      const current_ecr: CalendarRegisterLocalItem | undefined = this.currentTaskSubject.value.current;
      if (
        current_ecr &&
        current_ecr.id &&
        current_ecr.company_product_id &&
        current_ecr.call_link &&
        !this.isEcrIdOnMeetingsAlreadyStoppedFromMeetingMode(current_ecr.id)
      ) {
        this.startMeetingMode(+current_ecr.company_product_id);
      }
    }
  }

  private async forceIfNecessaryToSetupDoNotDisturbShortcutsOnMac(user: User) {
    if (this.isMeetingOngoing()) {
      return;
    }
    if (user.role.slug === 'worker' && this.platform.isElectronApp()) {
      const ok: boolean = await window.electron.checkDoNotDisturbShortcuts();
      if (!ok) {
        this.forceSetupDoNotDisturbShortcutsDialogRef = this.dialog.open(ForceSetupDoNotDisturbShortcutsDialogComponent, {
          width: '600px',
          disableClose: true,
        });
      } else if (this.forceSetupDoNotDisturbShortcutsDialogRef) {
        this.forceSetupDoNotDisturbShortcutsDialogRef.close();
      }
    }
  }

  private loadRequiredActions(): void {
    this.apiService
      .getMyActionsRequired()
      .pipe(takeUntil(this.destroy$))
      .subscribe((data: ActionsRequired): void => {
        this.actionsRequired.set(data);

        this.processPendingMeetingAttendances(data);
      });
  }

  private processPendingMeetingAttendances(data: ActionsRequired): void {
    const has_pending_meeting_attendances = data.pending_meeting_attendances_count > 0;

    if (has_pending_meeting_attendances) {
      this.checkAndOpenMeetingAttendanceDialog();
    }
  }

  /** API serialized: is_working_time llega como 1/0 desde la query, no como boolean JSON. */
  private isSerializedRegisterWorkingTime(item: EmployeeCalendarRegisterWithRelationsSerialized): boolean {
    const value = item.is_working_time as unknown;
    return value === true || value === 1;
  }

  private loadTodaysTasks(user: User) {
    const today = new Date();
    this.apiService
      .getEmployeeCalendarRegistersForUserSerialized(
        user.id.toString(),
        new Date(today.setHours(0, 0, 0)),
        new Date(today.setHours(23, 59, 59))
      )
      .subscribe((data: any) => {
        this.todaysTasks = data
          .filter((item: EmployeeCalendarRegisterWithRelationsSerialized) => this.isSerializedRegisterWorkingTime(item))
          .map((item: EmployeeCalendarRegisterWithRelationsSerialized): CalendarRegisterLocalItem => {
            let title: string = '';
            if (item.company_product_id != null) {
              title = item.company_product_title + ' - ' + (item.custom_title ?? item.wtc_name);
              if (item.company_product_phase_name != null) title += ' (' + item.company_product_phase_name + ')';
            } else title = item.wtc_name as string;

            return {
              id: item.id,
              title: title,
              start: new Date(item.from_date),
              end: new Date(item.to_date),
              color: item.company_product_color ?? item.ecr_wtc_color ?? item.wtc_color ?? '#ad2121',
              call_link: item.call_link,
              meet_mode: false,
              company_product_id: item.company_product_id,
            };
          })
          .sort((a: CalendarRegisterLocalItem, b: CalendarRegisterLocalItem) => a.start < b.start);

        this.updateCurrentTask();
      });
  }

  private async updateCurrentTask() {
    // Inicialitzem
    let duoCurrentTasks = this.currentTaskSubject.value;
    if (duoCurrentTasks == null || duoCurrentTasks == undefined) {
      // si no han estat inicialitzats... Això mai hauria de passar
      duoCurrentTasks = { current: null as any, next: null as any, showLiveIndicator: false };
    }

    if (this.todaysTasks.length > 0) {
      this.todaysTasks.sort((a: CalendarRegisterLocalItem, b: CalendarRegisterLocalItem) => a.start.getTime() - b.start.getTime());
      const current_task_index = this.getCurrentTaskIndex();
      const next_upcoming_index = this.getNextUpcomingTaskIndex();

      if (current_task_index != null) {
        duoCurrentTasks.current = this.todaysTasks[current_task_index];
        const nextIndex = current_task_index + 1;
        duoCurrentTasks.next = nextIndex < this.todaysTasks.length ? this.todaysTasks[nextIndex] : (null as any);
        duoCurrentTasks.showLiveIndicator = true;
      } else if (next_upcoming_index != null) {
        duoCurrentTasks.current = null as any;
        duoCurrentTasks.next = this.todaysTasks[next_upcoming_index];
        duoCurrentTasks.showLiveIndicator = true;
      } else if (this.isWorkingTimeNow()) {
        duoCurrentTasks = {
          current: null as any,
          next: null as any,
          showLiveIndicator: true,
        };
      } else {
        duoCurrentTasks = {
          current: null as any,
          next: null as any,
          showLiveIndicator: false,
        };
      }
    } else if (this.isWorkingTimeNow()) {
      duoCurrentTasks = {
        current: null as any,
        next: null as any,
        showLiveIndicator: true,
      };
    } else {
      duoCurrentTasks = {
        current: null as any,
        next: null as any,
        showLiveIndicator: false,
      };
    }

    this.currentTaskSubject.next(duoCurrentTasks);
    if (
      duoCurrentTasks.current &&
      duoCurrentTasks.current.id &&
      duoCurrentTasks.current.company_product_id &&
      duoCurrentTasks.current.meet_mode &&
      !this.isEcrIdOnMeetingsAlreadyStoppedFromMeetingMode(duoCurrentTasks.current.id) &&
      !this.isMeetingOngoing()
    ) {
      this.startMeetingMode(+duoCurrentTasks.current.company_product_id);
    }

    const now_ms = new Date().getTime();
    let sleep_ms = 30000;
    if (duoCurrentTasks.current != null && duoCurrentTasks.current.end != null) {
      sleep_ms = Math.max(duoCurrentTasks.current.end.getTime() - now_ms, 1000);
    } else if (duoCurrentTasks.next != null && duoCurrentTasks.next.start != null) {
      sleep_ms = Math.max(duoCurrentTasks.next.start.getTime() - now_ms, 1000);
    }
    await this.utils.sleep(Math.min(sleep_ms, 60000));

    this.updateCurrentTask();
  }

  private getNextUpcomingTaskIndex(): number | null {
    const now = new Date();
    const index = this.todaysTasks.findIndex((task: CalendarRegisterLocalItem) => task.start > now);
    return index === -1 ? null : index;
  }

  private getCurrentTaskIndex() {
    const now = new Date();
    let i: number = 0;
    let trobat: boolean = false;
    let fi: boolean = false;
    let res_index: number = null as any;

    while (!trobat && !fi) {
      if (this.todaysTasks[i].start <= now && this.todaysTasks[i].end > now) {
        trobat = true;
        res_index = i;
      } else if (this.todaysTasks[i].start > now) {
        trobat = true; // no és ben bé trobat... Vol dir que ens trobem en un buit entre tasques...
      }

      i++;
      fi = i >= this.todaysTasks.length;
    }

    return res_index;
  }

  private loadCurrentWorkingContract(user: User) {
    this.apiService.getActiveWorkingContract(user.id).subscribe(data => {
      this.currentWorkingContractActiveSubject.next(data);

      this.apiService.getWorkingContractScheduleForWeek(data.id, dayjs().startOf('isoWeek').format('YYYY-MM-DD')).subscribe({
        next: schedule_for_week => {
          this.currentWorkingContractWeeklySchedule = schedule_for_week.working_contract_weekly_schedule;
          this.loadTodaysTasks(user);
        },
        error: () => {
          this.currentWorkingContractWeeklySchedule = null;
          this.loadTodaysTasks(user);
        },
      });
    });
  }

  private loadCurrentGuardianStatus() {
    this.apiService.getMyGuardianStatus().subscribe(data => {
      this.currentGuardStatusSubject.next(data);
    });
  }

  private loadCurrentActiveCompanyProducts(id: number) {
    this.apiService.getUserCompanyProductsActiveRoles(id).subscribe(data => {
      this.active_company_products_signal.set(data);
    });
  }

  private isWorkingTimeNow(): boolean {
    const now = dayjs();
    const hour_with_decimal: number = now.hour() + now.minute() / 60;
    const current_day: DayOfWeekEnum = fromDayOfWeekIso(now.isoWeekday());
    const day_periods =
      this.currentWorkingContractWeeklySchedule?.working_contract_weekly_schedule_day_periods.filter(p => p.day_of_week === current_day) ??
      [];

    return day_periods.some(period => {
      const [start_hour, start_minute] = period.start_time.split(':').map(Number);
      const [end_hour, end_minute] = period.end_time.split(':').map(Number);
      const start_hour_with_decimal = start_hour + start_minute / 60;
      const end_hour_with_decimal = end_hour + end_minute / 60;

      return start_hour_with_decimal <= hour_with_decimal && end_hour_with_decimal > hour_with_decimal;
    });
  }

  private initViewportForceDesktopValidationListener(user: User, force_employees_to_use_desktop_app: boolean) {
    if (this.viewportForceDesktopListenerInitialized || typeof window === 'undefined') {
      return;
    }

    this.viewportForceDesktopListenerInitialized = true;

    window.addEventListener('resize', () => this.scheduleForceDesktopRecheck(user, force_employees_to_use_desktop_app));
    window.addEventListener('orientationchange', () => this.scheduleForceDesktopRecheck(user, force_employees_to_use_desktop_app));
  }

  private scheduleForceDesktopRecheck(user: User, force_employees_to_use_desktop_app: boolean) {
    if (this.forceDesktopRecheckTimeout) {
      clearTimeout(this.forceDesktopRecheckTimeout);
    }

    this.forceDesktopRecheckTimeout = setTimeout(() => {
      this.redirectToDesktopAppPageIfNecessary(user, force_employees_to_use_desktop_app);
    }, 150);
  }
}

interface CalendarRegisterLocalItem {
  id: number;
  title: string;
  start: Date;
  end: Date;
  color: string;
  call_link: string | undefined;
  meet_mode: boolean;
  company_product_id: number | undefined;
}
